import {STORIES} from '../dummy_data/sampleStories';
import { API, Storage, Auth } from 'aws-amplify';
import studentRecordService from './StudentRecordService';
import studentInformationService from './StudentInformationService';
import {searchSessionAudio} from './SpeechStream';
import {
  userTypes,
  assignmentTypes,
  activityStatus,
  locales,
  phraseTypes
} from '../constants/constants';
import { calculateAccuracy, getNonFlaggedErrors } from '../components/Reader/ReaderLibrary.js';
import * as Sentry from '@sentry/browser';
import {setStudentGradeLevelWithAREA} from "./readerCharacteristics.js";
import { statusType, getActivityStatus } from "./displayStatus";
import {formatGuidForSIS,isNotNullOrUndefined,logSentryException, promiseTimeout, removeEmphasisTags, removeOpeningBreakTag, removeOpeningEllipsis, removeProsodyTags } from './util';
import {isiPad} from './environment';
import device from './device/device';
import { logSessionInfo } from './logger';
import {filterByDate, filterByLocale, getSchoolYear} from '../components/Reports/util/validation';
import {inBenchmarkScreeningWindow, inCurrentScreeningWindow} from './screeningWindowService';
import { getNextEarlyReaderStory, isEarlyReaderInSequence } from 'Services/EarlyReaderService';
import { getSaxonSequenceStart } from './saxonService';
import { flattenChildrenAndGrandchildren } from './APIUtils';
import get from 'lodash.get';
import { isScorableEarlyReaderStory } from './EarlyReaderService';
import getArmScore from '../components/Reports/util/getArmScore';
import { GetStoryBranding, SetBranding } from './BrandingService';
import { redirectToLoginOrigin, saveLoginOrigin } from './loginService.js';
import i18n from '../i18n/i18n.js';
import { defaultEntitlements, developingEntitlements } from '@/store/slices/UserEntitlementSlice';
import { createRandomSpeechMarks, TTS_ENGINE_TYPE } from './generativeVoiceService';

export const DEFAULT_LOCALE = "en-us";

const DEFAULT_USER_STORE_DATA = {
  userId: null,
  sessionId: null,
  userType: null,
  userAgent: null,
  districtId: null,
  schoolId: null,
  sessionStartTime: null,
  sessionData: {},
  scoreType: 'ARM', // Default to ARM if not specified
};

var stories_store = [];
var utterance_store = {};
var userData_store = {...DEFAULT_USER_STORE_DATA};

const defaultSORConfig = {
  enableERPictures: true,
  enablePracticePictures: true,
  enableGiveWord: true,
  enableFandP: false,
  enableDRA: false,
  enableLexile: false,
  enableExtendedColumns: false,
  selectedStandard: "COMMON_CORE",
  interventionFrequency: "STANDARD",
  customARMLevelMapping: null,
};

//Set this to true in order to load the stories from sampleStories.js
const GET_STORIES_LOCALLY = false;

//Set this to true in order to load a hard coded assignment for all students, entering forced assessment mode
const FORCE_ASSESSMENT = process.env.FORCE_ASSESSMENT;

//Delete userData_store
export function clearUserData(){
  userData_store = {...DEFAULT_USER_STORE_DATA};
  Auth.signOut()
    .then(() => window.history.pushState(null, '',  "/"))
    .catch(err => console.error("ERROR SIGNING OUT", err));
}

//return the userData_store
export function getUserData(){
  return userData_store;
}

export function setUserData(key, value){
  userData_store = {
    ...userData_store,
    [key]: value
  };
}

export function setSessionActivityId(inActivityId, assessmentMode){
  userData_store.activityId = inActivityId;

  //Initialize the inference service session
  if(!assessmentMode && process.env.INFERENCE_SERVICE_URL && process.env.INFERENCE_SERVICE_EAST_2){
    const baseURL = getUserData().region && getUserData().region === 'us-east-2' ? process.env.INFERENCE_SERVICE_EAST_2 : process.env.INFERENCE_SERVICE_URL;
    const url =  baseURL + "/init/" + inActivityId;
    const requestBody = {
      isESL: getUserData().isESL ?? false,
    }
    if (getUserData().interventionSelectionWCPMScore) {
      requestBody.prev_equatedwcpm = getUserData().interventionSelectionWCPMScore;
    }
    return fetch(url,{
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      mode: 'no-cors'
    })
    .catch((err)=>console.error("Error priming inference for activity",inActivityId, err));
  }
}

export const defaultConfig = {
  NWF_CONFIG: [true,true,false,false,false,false,false,false,false,false],
  LISTENING_COMP_CONFIG: [true,true,true,false,false,false,false,false,false,false],
  READING_COMP_CONFIG: [false,false,false,true,true,true,false,false,false,false],
  SPELLING_CONFIG: [true,true,true,true,true,true,true,true,true,false],
  LETTERNAME_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  VOCABULARY_SCREENER_CONFIG: [true,true,true,true,true,true,true,true,true,false],
  VISUAL_ATTENTION_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  LETTER_NAMING_AND_SOUNDS_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  LISTENING_COMP_MC_CONFIG: [ true, true, true, true, true, true, true, true, true, false],
};

async function getAppSettings(){
  return await studentRecordService.getEntitlements(9999999,9999999);
}

export function setV2InterventionUserParams(activities) {
  let interventionSelectionWCPMScore;
  let interventionSelectionARMScore;

  if (activities) {
    for (let i = 0; i < activities.length; i++) {
      if (!interventionSelectionWCPMScore && activities[i].scores) {
        interventionSelectionWCPMScore = activities[i].scores.equatedWCPM;
      }

      if (!interventionSelectionARMScore && activities[i].scoreEstimates && activities[i].scoreEstimates.ARM_estimate) {
        interventionSelectionARMScore = activities[i].scoreEstimates.ARM_estimate;
      }

      if (interventionSelectionWCPMScore && interventionSelectionARMScore){
        break;
      }
    }
  }

  if (interventionSelectionWCPMScore) {
    userData_store.interventionSelectionWCPMScore = interventionSelectionWCPMScore;
  }

  if (interventionSelectionARMScore) {
    userData_store.interventionSelectionARMScore = interventionSelectionARMScore;
  }
}

async function storeUserData(authData, resp){
  let iPad = isiPad();
  userData_store = {
    email: resp.attributes.email,
    userName: resp.username,
    userId: resp.attributes['custom:sisId'] || resp.attributes.sub,
    userType: resp.attributes['custom:userType'] || userTypes.TEACHER,
    firstName: resp.attributes['custom:firstName'],
    lastName: resp.attributes['custom:lastName'],
    assessment: resp.attributes['custom:assessment'],
    skipMagic: resp.attributes.skipMagic,
    correlationid: resp.attributes.correlationid,
    userAgent: navigator.userAgent,
    sessionId: getSessionID(),
    sessionStartTime: Date.now(),
    browser: device.getBrowserForSession(),
    sessionData: {
      cognitoUserSession: authData.signInUserSession,
      authData: authData,
      isiPad: iPad,
      useIpadFallbacks: iPad && parseFloat(device.browser.version) < 13,
    },
  };

  //Special case for side-loaded admins
  if(!resp.attributes['custom:sisId']){
    userData_store.skipMagic = true;
    userData_store.sideloadedUser = true;
  }

  let connection = {}
  if(navigator.connection){
    connection = {
      downlink: navigator.connection.downlink,
      effectiveType: navigator.connection.effectiveType,
      rtt: navigator.connection.rtt,
      saveData: navigator.connection.saveData
    }
  }

  userData_store.connection = connection;
  console.log(JSON.stringify(getUserData().connection));

  Sentry.configureScope((scope) => {
    scope.setUser(userData_store);
    scope.setTag("user-id", userData_store.userId);
    scope.setTag("user-type", userData_store.userType);
    scope.setTag("user-school", userData_store.schoolId);
    scope.setTag("user-district", userData_store.districtId);
    scope.setTag("app-version", process.env.gitHash);
  });
  console.log(process.env.gitHash);

  //NUCLEAR OPTION: Redirect the user to the site maintenance page
  userData_store.appSettings = await getAppSettings();
  if(userData_store.appSettings && userData_store.appSettings.siteUnderMaintenance){
    underMaintenance();
  }

  if(userData_store.userType === userTypes.STUDENT){
    return Promise.resolve(userData_store);
  }else{
    try {
      // Fetch schools and district information
      const schoolsAndDistricts = await studentInformationService.getSchoolsAndDistricts(
        resp,
        authData,
        resp.username,
        userData_store.userId,
        userData_store.userType,
        resp.attributes.skipMagic
      );

      // Update the user data store with the schools and district data
      updateUserStoreWithSchools(userData_store, schoolsAndDistricts);

      // Set some Sentry tags for tracking
      Sentry.setTag("school-id", userData_store.schoolId);
      Sentry.setTag("district-id", userData_store.districtId);

      // Fetch entitlements using district and school ids
      const entitlements = await withRetry(() => studentRecordService.getEntitlements(
        schoolsAndDistricts.district.id,
        schoolsAndDistricts.schools[0].id
      ));
      // Update user data store with entitlements and related configuration
      updateUserStoreWithEntitlements(userData_store, entitlements);

      // Return the updated store
      return Promise.resolve(userData_store);
    } catch (error) {
      // Handle errors (e.g., log them and rethrow or handle appropriately)
      Sentry.captureException(error);
      throw error;
    }
  }
}

function updateUserStoreWithSchools(userData_store, data) {
  // Destructure needed properties for clarity
  const { permissions, reportingRole, schools, district, subdistrictId } = data;
  userData_store.permissions = permissions;
  userData_store.reportingRole = reportingRole;
  userData_store.schools = schools;
  userData_store.district = district;
  userData_store.districtId = district.id;
  userData_store.subdistrictId = subdistrictId;
  userData_store.schoolId = schools[0].id;
}

/**
 * We are keeping the entitlements in the userData_store object until we can refactor all code to use the redux store,
 * Legacy code still are using getUserData() to get the entitlements.
 * @param {*} userData_store
 * @param {*} entitlements
 */
function updateUserStoreWithEntitlements(userData_store, entitlements = {}) {
  // extract entitlements with default values, ensuring each value is at least the default value if the service returned null or undefined.
  const branding = entitlements.branding ?? defaultEntitlements.branding
  const reportingLanguage =  entitlements.reportingLanguage ?? defaultEntitlements.reportingLanguage
  const dyslexiaEnabled = entitlements.dyslexiaEnabled ?? defaultEntitlements.dyslexiaEnabled
  const partner =  entitlements.partner ?? defaultEntitlements.partner
  const dyslexiaParentLetterEnabled = entitlements.dyslexiaParentLetterEnabled ?? defaultEntitlements.dyslexiaParentLetterEnabled
  const parentReportEnabled = entitlements.parentReportEnabled ?? defaultEntitlements.parentReportEnabled
  const legacyReportsEnabled = entitlements.legacyReportsEnabled ?? defaultEntitlements.legacyReportsEnabled
  const texasResources = entitlements.texasResources ?? defaultEntitlements.texasResources
  const schoolYearStartDate=entitlements.schoolYearStartDate ?? defaultEntitlements.schoolYearStartDate
  const sorConfig=entitlements.sorConfig ?? defaultSORConfig
  const resourceProviders=entitlements.resourceProviders ?? defaultEntitlements.resourceProviders
  const statePercentileCuts=entitlements.statePercentileCuts ?? defaultEntitlements.statePercentileCuts
  const prereaderAudioEnabled=entitlements.prereaderAudioEnabled ?? defaultEntitlements.prereaderAudioEnabled
  const useLatestARM=entitlements.useLatestARM ?? defaultEntitlements.useLatestARM
  const demoLockdown=entitlements.demoLockdown ?? defaultEntitlements.demoLockdown
  const calibrationEnabled=entitlements.calibrationEnabled ?? defaultEntitlements.calibrationEnabled
  const demoMode=entitlements.demoMode ?? defaultEntitlements.demoMode
  const iesEnabled=entitlements.iesEnabled ?? defaultEntitlements.iesEnabled
  const districtAdminBadgingEnabled=entitlements.districtAdminBadgingEnabled ?? defaultEntitlements.districtAdminBadgingEnabled
  const expandedEarlyReaderEnabled=entitlements.expandedEarlyReaderEnabled ?? defaultEntitlements.expandedEarlyReaderEnabled
  const badgingEnabled=entitlements.badgingEnabled ?? defaultEntitlements.badgingEnabled
  const windowDisabledByGrade=entitlements.windowDisabledByGrade ?? defaultEntitlements.windowDisabledByGrade
  const showBadNetworkResponse=entitlements.showBadNetworkResponse ?? defaultEntitlements.showBadNetworkResponse
  const curriculum=entitlements.curriculum ?? defaultEntitlements.curriculum
  const fourCutlineCategoriesEnabled=entitlements.fourCutlineCategoriesEnabled ?? defaultEntitlements.fourCutlineCategoriesEnabled
  const allowLetterNamingAndSoundsRescoring=entitlements.allowLetterNamingAndSoundsRescoring ?? defaultEntitlements.allowLetterNamingAndSoundsRescoring

  const curriculumOnboardCompleted=entitlements.curriculumOnboardCompleted ?? defaultEntitlements.curriculumOnboardCompleted
  const assessmentOnboardingCompleted=entitlements.assessmentOnboardingCompleted ?? defaultEntitlements.assessmentOnboardingCompleted
  const curriculumRightsCertificationCompleted=entitlements.curriculumRightsCertificationCompleted ?? defaultEntitlements.curriculumRightsCertificationCompleted

  const amiraTutorSettings=entitlements.amiraTutorSettings
  const individualLearningPathMode=entitlements.individualLearningPathMode

  const scoreType=entitlements.scoreType ?? defaultEntitlements.scoreType
  
  // Add the developing entitlements 
  Object.keys(developingEntitlements).forEach(key => {
    if (!entitlements.subscriptions) {
      entitlements.subscriptions = {};
    }
    if (entitlements.subscriptions[key] == null) {
      entitlements.subscriptions[key] = developingEntitlements[key];
    }
  });

  // Set branding and language early so they can be used application-wide
  SetBranding(branding);
  i18n.changeLanguage(reportingLanguage);

  // Update the user store with entitlements
  Object.assign(userData_store, {
    reportingLanguage,
    dyslexiaEnabled,
    branding,
    partner,
    dyslexiaParentLetterEnabled,
    parentReportEnabled,
    legacyReportsEnabled,
    texasResources,
    schoolYearStartDate,
    sorConfig,
    resourceProviders,
    statePercentileCuts,
    prereaderAudioEnabled,
    useLatestARM,
    demoLockdown,
    calibrationEnabled,
    demoMode,
    iesEnabled,
    districtAdminBadgingEnabled,
    expandedEarlyReaderEnabled,
    badgingEnabled,
    windowDisabledByGrade,
    showBadNetworkResponse,
    curriculum,
    fourCutlineCategoriesEnabled,
    allowLetterNamingAndSoundsRescoring, // above this is to keep old code working
    entitlements, // Add the full entitlements object for for redux state and future code
    amiraTutorSettings: entitlements.amiraTutorSettings,
    individualLearningPathMode: entitlements.individualLearningPathMode,
    scoreType: entitlements.scoreType
  });

  // Set derived values
  userData_store.saxonEnabled = resourceProviders?.includes("Saxon");

  // Special adjustments for specific demo users
  if (userData_store.userName === "hmh.demo@parkside.com") {
    userData_store.curriculum = null;
    userData_store.partner = "HMH";
    userData_store.resourceProviders = ["IntoReading"];
    if (userData_store.sorConfig) {
      userData_store.sorConfig.selectedStandard = "COMMON_CORE";
    }
  }
}

function authenticateUser(authData){
  return Auth.currentAuthenticatedUser()
}

// save the user data
export async function saveUserData(authData,retries=1){
  // if authData doesn't have a userType, or the userType is invalid, we will default to STUDENT,
  // on the other hand, if the userType does exist, then we assign the entire authData to userData_store.
  // for now all routes are exposed for user type STUDENT

  return authenticateUser(authData)
  .then(async resp => {
    return storeUserData(authData, resp)
  })
  .catch(err => {
    console.log("Error in auth",err);
    if(retries){
      console.log("Retrying auth",retries);
      return saveUserData(authData,retries-1);
    }else{
      return Promise.reject(err);
    }
  });
}

export function magicGradeToAmiraGrade(inMagicGrade){
  const gradeLookup = [
    {label: "Kindergarden", grade: 0},  //NOTE: Misspelling intentional - this is how magic returns it.
    {label: "Kindergarten", grade: 0},
    {label: "First grade", grade: 1},
    {label: "Second grade", grade: 2},
    {label: "Third grade", grade: 3},
    {label: "Fourth grade", grade: 4},
    {label: "Fifth grade", grade: 5},
    {label: "Sixth grade", grade: 6}
  ];

  let grade = gradeLookup.filter(lookup => {
    return lookup.label === inMagicGrade;
  });

  let returnGrade = grade.length ? grade[0].grade : 6;

  return returnGrade;
}

function getSessionID () {
  // add user data when
  return `Amira-Field-Trial:${Date.now()}`;
}

// returns a deepCopy of the given object
// this can help us guard against accidentally mutating the store
function deepCopy(obj){
  return JSON.parse(JSON.stringify(obj));
}

// return all possible stories - NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
export function getStories(){
  if(GET_STORIES_LOCALLY){
    return Promise.resolve(STORIES);
  } else{
    return Promise.reject(new Error("Tried to get all stories in existance from the back end!"));
  }
}

function addStoryAttributes(story){
  //TODO: consider adding this on the backend instead
  story.locale = getUserData().locale;
  if(story.type == assignmentTypes.SPELLING){
    story.nonAudio = true;
  }

  if(story.tags && story.tags.length){
    story.brand = GetStoryBranding(story.tags);
  }

  return story;
}

const tutorWhitelist = [
  { 
    "storyid": "93405360-362f-11e9-bb3b-e7e2c0de6bee",
    "title": "The Life Cycle of Butterflies",
    "AREA_85": 8.6,
    "AREA_95": 10.2,
  },
  {
    "storyid": "d2669700-1ed8-11eb-83fe-57c4f9003880",
    "title": "Mustache Day",
    "AREA_85": 7.4,
    "AREA_95": 10.1,
  },
  {
    "title": "Animal Homes",
    "storyid": "73d63580-f9b0-11ea-8ba7-4f55b44592d0",
    "AREA_85": 6.8,
    "AREA_95": 8.7,
  },
  {
    "title": "FYI for kids: Bigger than an elephant",
    "storyid": "aa9a8ee0-f9c9-11ea-9b55-25ca75ea58d7",
    "AREA_85": 8.1,
    "AREA_95": 10.1,
  },
  {
    "title": "Bumblebees",
    "storyid": "f9ce3830-0974-11eb-8a99-238a1e6e21be",
    "AREA_85": 8,
    "AREA_95": 10.9,
  }
];

// divides story into a given number (default 3) of phrase pages
export function paginateStory(story, phrasesPerPage=3, wordsPerPhrase=null){
  let pages = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    let currentPhrase = "";
    let currentPage = [];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];

      //Map logical item types to display types
      if(chapter.items && chapter.items[j]){
        chapter.items[j].type = getDisplayType(chapter.items[j].type);
        if(chapter.items[j].instructions && chapter.items[j].instructions.type){
          chapter.items[j].instructions.type = getDisplayType(chapter.items[j].instructions.type);
        }
      }

      if(!wordsPerPhrase || currentPhrase.split(" ").length + phrase.split(" ").length > wordsPerPhrase){
        if(currentPhrase !== ""){
          currentPage.push(currentPhrase);
        }
        currentPhrase = phrase;
      } else {
        currentPhrase = currentPhrase + " " + phrase;
      }

      // list of item types that should be one phrase per page
      const typesWithOnePhrasePerPage = [phraseTypes.NONSENSE_WORD_REPETITION,
        phraseTypes.CHANGE_ONE_PART,
        phraseTypes.WORD_PART_DELETION,
        phraseTypes.UP_AND_DOWN
      ];

      if((currentPhrase === "") && typesWithOnePhrasePerPage.includes(get(chapter, "items["+j+"].type"))){
        currentPage.push(currentPhrase);
        pages.push(currentPage);
        currentPhrase="";
        currentPage = [];
      }else{
        if(currentPage.length >= phrasesPerPage){
          pages.push(currentPage);
          currentPage = [];
        }

        // if this is the last phrase add the current phrase and page we are working on even if they are not long enough yet
        if(j >= chapter.phrases.length - 1){
          currentPage.push(currentPhrase);
          pages.push(currentPage);
        }
      }
    }
  }

  return {
    title: story.title,
    author: story.author,
    alternateIntroText: story.alternateIntroText,
    pages: pages,
    items: story.chapters[0].items,
    phrases: story.chapters[0].phrases,
    questions: story.questions,
    storyId: story.storyId,
    type: story.type,
    version: story.version,
    grade: story.grade,
    nonAudio: story.nonAudio,
    brand: story.brand,
    tags: story.tags,
    metadataOverrides: story.chapters[0].overrides,
  };
}

  //This method maps the abstract types of an item to our display types
function getDisplayType(type){
    switch(type){
      case 'blending':
        return "video";
      case 'wordList':
        return "word";
      case 'letterSound':
        return "sound";
      case 'ran':
        return "RAN";
      case 'spelling':
        return "textInput";
      case 'quiz':
        return 'quiz';
      case 'readAloudVideo':
        return 'readAloudVideo';
      default:
        return type;
    }
}

// returns a story with all phrases concatinated into one single phrase
function squashStory(story){
  let metaPhrase = "";
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      if(!(i == 0 && j == 0)){
        metaPhrase += " ";
      }
      metaPhrase += phrase;
    }
  }
  return({
    title: story.title,
    author: story.author,
    storyId: story.storyId,
    version: story.version,
    type: story.type,
    storyTags: story.tags,
    chapters: [
      {phrases: [metaPhrase]}
    ],
  });
}


//TODO: this should not be necessary when we are getting back scored story data from the back end
// unwrap phrases with possibility of adding meta data. originally was considering keeping errors,
// mispronunciations etc in here, but since we are mutating them parallel arrays makes it easier for now
function unwrapPhrases(activity){
  let story = activity.story;
  let errors = activity.errors;
  let phrases = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      let phraseErrors = errors ? errors[j] : [];   //relying on the fact that we are not actually subdividing by chapter, and that the errors array is not currently indexed by chapter
      let markedPhrase = {
        text: phrase,
      }
      phrases.push(markedPhrase);
    }
  }
  return phrases;
}

//returns an object containing a paginated story, an audio file of a student reading it, and amira's guess at errors
// to be used in teacher correction UI
export function getScoredStory(activityId){
  return studentRecordService
    .getActivity(activityId)
    .then(({data: { getActivity }}) => {
      const activity = getActivity[0];
      const { phrases } = activity.story.chapters[0];
      const { errors } = activity;
      const reduxState = require('../store').default.getState();
      const students = reduxState.reports?.roster?.data?.student || [];

      //Handle empty error elements
      errors.forEach((error, i) => {
        if(error.length === 0){
          error = new Array(phrases[i].split(' ').length).fill(true);
        }
      });

      // Parse audio URL
      const rawUrl = activity?.audio?.url;
      let parsedUrl = null;
      if (typeof rawUrl === 'string') {
        if (rawUrl.startsWith('https://')) {
          parsedUrl = rawUrl;
        } else {
          const match = rawUrl.match(/https[^}"]+/);
          parsedUrl = match ? match[0] : null;
        }
      }

      const scoredStory = ({
        ...activity,
        audio: { url: parsedUrl },
        squashedStory: squashStory(activity.story),
        mispronunciations: activity.mispronunciations || [],
        skips: activity.skips || [],
        selfCorrections: activity.selfCorrections || [],
        phrases: unwrapPhrases(activity),
        sidebarStatus: getActivityStatus(activity,false),
        updatedAt: new Date(activity.updatedAt),
        flaggedPhrases: activity.flagging,
        wcpmScore: activity.scores.wcpmScore,
      });

      const studentData = students.find(s => s.id === scoredStory.studentId);
      if (studentData) {
        scoredStory.student = {
          studentId: scoredStory.studentId,
          firstName: studentData.first_name,
          lastName: studentData.last_name,
          userName: studentData.userName || `${studentData.first_name}.${studentData.last_name}`,
        };
      } else {
        console.warn(`Student data not found in Redux state for studentId: ${scoredStory.studentId}`);
        scoredStory.student = {
          studentId: scoredStory.studentId,
          firstName: "Student",
          lastName: "",
          userName: "student",
        };
      }

      return scoredStory;
    });
}

//stub to save teacher corrections to scoring
export function saveScoredAssessment(activityId, errors, skips, selfCorrections, mispronunciations, timeRead, flat = true){
  let formattedErrors = errors ? errors : [[]]; // TODO modify backend so that this is not necessary

  if(flat){
    formattedErrors = [errors.flat(4)];
  }

  //Remove any empty error elements (so we don't implicitly pad)
  formattedErrors = formattedErrors.filter(function (el) {
    return el.length > 0;
  });

  return studentRecordService.scoreActivity(activityId, formattedErrors, skips, selfCorrections, mispronunciations, timeRead);
}

//Using this helper function because the studentIds coming from SIS
//have a different format than the ones coming from the activities call
//TODO: We should store the studentId the same way in SIS and "ActivitiesDB" so we don't have to format these ids
export function getFormatedId(id) {
  return String(id).replace(/\-/g, '').toUpperCase();
}

export function getParentReport(studentId, equated=false) {
  return studentRecordService.getParentReport(studentId, equated).then((data) => {
    return data;
  }, (error) => {
    console.log(error);
    throw error
  });
}

export function getAllAssessmentsForStudents(students, limit=null, type=["assessment"], equated=false, locale=null, startDate, endDate) {
  return getActivitiesWithStudentInfo(students, limit, type, ["assigned","scored","rescored","not_started","in_progress","scoring","underReview","reassess"], equated, locale, startDate, endDate);
}

export function getValidDyslexiaAssessmentsForStudents(students, cachedDyslexiaData, limit=null) {
  if(cachedDyslexiaData && cachedDyslexiaData.activities){
    return Promise.resolve(cachedDyslexiaData.activities)
  }
  return getDyslexiaActivitiesForStudents(students, limit);
}

export function getDyslexiaActivitiesForStudents(students, limit=null) {
  const studentIds = students.map(({ id }) => getFormatedId(id));
  return studentRecordService.dyslexiaActivities(studentIds, limit)
    .then((activities) => {
      return activities;
    });
}

export function filterDyslexiaAssessments(students, activities, window){
  // all dyslexia activities within the school year
  activities = filterByDate(activities, window);
  if(Array.isArray(activities) && activities.length > 0){
    let englishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && !isSpanishActivity(activity)));
    });
    englishActivities = englishActivities.filter(act=> act);

    let spanishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && isSpanishActivity(activity)));
    });
    spanishActivities = spanishActivities.filter(act=> act);

    let returnActivities = englishActivities.concat(spanishActivities);
    //return the activities with the student information
    return returnActivities.map((activity) => ({
      ...activity,
      student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
    }));
  }
  return [];
}

export function isSpanishActivity(activity){
  return activity.tags && activity.tags.includes("ES_MX");
}

export function getValidActivitiesForStudents(students, limit=null, type=["assessment","tutor"], equated=false, locale=null, startDate, endDate) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, ["scored","rescored"], equated, locale, startDate, endDate)
    .then((activities) => {
      console.log("getActivities",activities)
      let filtered = activities.filter(activity => activity.flagging.some(f => f.triggered));
      return activities.filter((activity)=> activity && activity.scores && activity.scores.wcpmScore > 0 && activity.displayStatus !== statusType.UNASSIGNED);
    });
}

export function getParentActivitiesForStudents(students, limit=null, type=["assessment", "dyslexia", "letterNamingAndSounds"], equated=true, locale=null, startDate, endDate, statuses=null, displayStatuses=null, ) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, statuses, equated, locale, startDate, endDate, displayStatuses)
    .then((activities) => {
      console.log("getActivities",activities)
      return activities.filter((activity)=> {
        // If the student is a prereader and has a dyslexia score, we want to show the dyslexia score
        if(activity?.displayStatus === 'prereader' &&
          activity?.children?.filter( childActivity => childActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(childActivity.scores.armScore))?.length > 0){
          return true;
        }
        // Also want to handle the case where the student is a prereader and has a sibling with a dyslexia score (When taking )
        if(activity?.displayStatus === 'prereader' &&
          activity?.siblings?.filter( siblingActivity => siblingActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(siblingActivity.scores.armScore))?.length > 0){
          return true;
        }
        return activity &&
          activity.scores &&
          activity.scores.wcpmScore > 0 &&
          activity.displayStatus !== statusType.UNASSIGNED
      });
    });
}

export function getProgressActivitiesForStudents(students, limit=null, type=["assessment","tutor","letterNamingAndSounds"], equated=false, locale, scale) {
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const isARM = scale === "ARM";

  const currentSchoolYear = getSchoolYear();
  const { startDate, endDate } = currentSchoolYear

  return studentRecordService.progressActivities(studentIds, limit, type, equated, isARM, startDate, endDate)
    .then((activities) => {
      activities = filterByDate(activities);
      activities = filterByLocale(activities,locale);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    }).then((activities) => {
      return activities.filter((activity)=> {
        const armScore = getArmScore(activity.student.id, activity, locale);
        const isValidARM = armScore || armScore == 0 || activity.type === assignmentTypes.LETTER_NAMING_AND_SOUNDS;
        return activity && activity.scores && ((activity.scores.wcpmScore > 0) || isScorableEarlyReaderStory(activity.story) || isValidARM);
      });
    });
}

export function getActivitiesWithStudentInfo(students, limit=null, type=["assessment","tutor"], status=["scored","rescored","assigned","not_started","in_progress","scoring","reassess"], equated=false, locale, startDate, endDate, displayStatus=null){
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const startDateUTC = startDate && new Date(startDate).toISOString();
  const endDateUTC = endDate && new Date(endDate).toISOString();
  return studentRecordService.studentActivities(studentIds, limit, type, status, equated, startDateUTC, endDateUTC, displayStatus)
    .then((activities) => {
      activities = filterByDate(activities);
      let allActivitiesFilteredByDate = activities;
      if(locale){
        activities = filterByLocale(activities,locale);
      }

      //Take out assignment stubs
      activities = activities.filter((activity)=>activity.displayStatus !== statusType.UNASSIGNED);

      //Add siblings
      activities = addSiblingsToChildren(activities, allActivitiesFilteredByDate, locale);

      // Flatten mis-parented grandchild activities
      activities = flattenChildrenAndGrandchildren(activities);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    });
}


//TODO: Account for locale here
function addSiblingsToChildren(childActivities, parentActivities, locale) {

  childActivities.forEach(childActivity => {

    const parentActivity = parentActivities.find(parentActivity =>
      parentActivity.activityId === childActivity.parentId
    );

    if (parentActivity) {
      let siblings = parentActivity.children.filter(sibling =>
        sibling.activityId !== childActivity.activityId && sibling.locale === childActivity.locale
      );

      //Ensure we're only adding siblings that match the target activity locale
      if (locale) {
        siblings = filterByLocale(siblings, locale);
      }

      childActivity.siblings = siblings; // Assign the siblings to the childActivity object here
    }

  });

  return childActivities;
}

export function endUserSession(){
  clearUserData();
  const showAppSessionEndedPage = true;
  // Only override the login origin if the user is not "skipMagic" or set as a sideloaded user
  // For now, we are forcing all users to logoff through the login origin
  // if ((userData_store && !userData_store.skipMagic) && userData_store.sideloadedUser) {
    saveLoginOrigin(`${process.env.ISTATION_EDUCATOR_URL}Account/LogOff`);
  // }
  redirectToLoginOrigin(null, showAppSessionEndedPage);
}

export function underMaintenance(){
  window.location.href = "/underMaintenance.html";
}

export function withRetry(fn, retries=3, delay=1000){
  return fn().catch((err) => {
    if(retries){
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(withRetry(fn,retries-1,delay*2));
        },delay);
      });
    } else {
      return Promise.reject(err);
    }
  });
}

function getUtteranceStoreKey(text, includeSpeechMarks, locale){
  return text.replace(/\s/g, '') + "_" + includeSpeechMarks + "_" + locale;
}

//can be called by itself to preload an utterance, just throw away the return value
export async function getUtterance(text, includeSpeechMarks=false, locale="en-us", timeout=10000, useGenerative=false){
  if(!text) return Promise.reject(new Error("no utterance provided",{text}));
  const key = getUtteranceStoreKey(text, includeSpeechMarks, locale);
  if(utterance_store.hasOwnProperty(key) && text){
    // if we've already stored this utterance, pull it from the cache
    // make sure to make it thenable
    return new Promise((resolve, reject) => {
      let returnValue = deepCopy(utterance_store[key]);
      returnValue.id = new Date().getTime();
      resolve(returnValue);
    }); //copy the utterance before returning to eliminate any chance of mutating the store
  } else {
    //Invoke the text to speech API
    if(!text.includes("<speak>")){
      text = '<speak>'+text+'</speak>';
    }
    let args = {
      textToSpeak: text,
      includeSpeechMarks: includeSpeechMarks,
      locale: locale,
      engine: useGenerative && locale.toLowerCase() === 'en-us' ? TTS_ENGINE_TYPE.generative : TTS_ENGINE_TYPE.standard,
    }

    //TODO: Think about this timeout and make sure we have retries.
    return promiseTimeout(getTextToSpeech(args),timeout)
    .then(async resp => {
      //Build the audio buffer
      let uInt8Array = new Uint8Array(resp.speech_audio.AudioStream.data);
      let arrayBuffer = uInt8Array.buffer;
      let blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      let audioURL = URL.createObjectURL(blob);

      let utterance = {
        speech_marks: resp.speech_marks,
        speech_audio: resp.speech_audio.AudioStream.data,
        audioURL: audioURL,
        id: new Date().getTime(),
        text: resp.text
      };

      //cache the utterance
      utterance_store[key] = utterance;
      return Promise.resolve(deepCopy(utterance));
    })
    .catch(err => {return Promise.reject(err)});
  }
}

function handleVoiceOverride(locale){
  const override = getUserData().voiceOverrides;
  if(!override || override.length === 0){
    return locale;
  }else{
    const newLocale = override.find(o=>o.locale === locale);
    return newLocale ? newLocale.override : locale;
  }
}

export async function getTextToSpeech(args,retries=4) {
  let inText = args.textToSpeak;

  inText = inText.replace(/[""]/g, '"');

  let returnSpeechMarks = args.includeSpeechMarks;
  let locale = handleVoiceOverride(args.locale);
  let engine = args.engine;

  if (engine === TTS_ENGINE_TYPE.generative) {
    // Generative voice does not support prosody tags, emphasis tags, opening break tags, or opening ellipsis
    inText = removeProsodyTags(inText);
    inText = removeEmphasisTags(inText);
    inText = removeOpeningBreakTag(inText);
    inText = removeOpeningEllipsis(inText);
  }

  //Get a transcription
  let apiName = 'lexaSpeech';
  let path = '/TextToSpeech';
  let myInit = { // OPTIONAL
    headers: {  'Content-Type': 'application/json'
    },
    body: {inText, locale, engine},
    returnSpeechMarks,
    locale: locale,
    response: false // OPTIONAL (return entire response object instead of response.data)
  };

  const utteranceLogParams = {
    text: inText,
    locale,
    includeSpeechMarks: returnSpeechMarks,
    engine
  }

  // storeUtteranceLog(utteranceLogParams);
  console.log("TTS API:", utteranceLogParams);

  return API.post(apiName, path, myInit)
  .then(resp => {
    resp.text = inText;
    if (
      !resp.speech_marks ||
      resp.speech_marks.length === 0 ||
      !resp.speech_marks[0].words ||
      resp.speech_marks[0].words.length === 0
    ) {
      // Ensure we always have some sort of speech marks
      resp.speech_marks = createRandomSpeechMarks();
    }

    return Promise.resolve(resp);
  })
  .catch(err => {
    if(retries){
      console.log("RETRYING TTS")
      return getTextToSpeech(args,retries-1);
    }else{
      return Promise.reject(err);
    }
  });
}